import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography
} from "@mui/material";

type AddLinkDialogProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (url: string) => void;
};

export default function AddLinkDialog({
  open,
  onClose,
  onAdd
}: AddLinkDialogProps) {
  const [linkInput, setLinkInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    const url = linkInput.trim();
    const urlPattern = /^(https?:\/\/)[^\s]+$/i;
    
    if (!url) {
      setError("Please enter a URL.");
      return;
    }
    
    if (!urlPattern.test(url)) {
      setError("Please enter a valid URL (must start with http:// or https://).");
      return;
    }

    onAdd(url);
    setLinkInput("");
    setError(null);
  };

  const handleClose = () => {
    setLinkInput("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>Add a Link</DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <TextField
          label="Paste your URL"
          value={linkInput}
          onChange={e => setLinkInput(e.target.value)}
          fullWidth
          margin="normal"
          autoFocus
          sx={styles.textField}
          helperText="Enter any valid URL - special characters will be handled automatically"
        />
        {error && (
          <Box mt={2} sx={styles.errorBox}>
            <Typography variant="body2" color="error" sx={styles.errorText}>
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={handleAdd} disabled={!linkInput.trim()} variant="contained" color="primary" sx={styles.addButton}>
          Add
        </Button>
        <Button onClick={handleClose} sx={styles.cancelButton}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const styles = {
  dialogTitle: {
    pb: 1
  },
  dialogContent: {
    paddingTop: 2,
    paddingBottom: 2
  },
  textField: {
    marginTop: 1,
    marginBottom: 1
  },
  errorBox: {
    marginTop: 2
  },
  errorText: {
    color: "error.main"
  },
  dialogActions: {
    justifyContent: "flex-end",
    padding: "16px"
  },
  addButton: {
    minWidth: 80
  },
  cancelButton: {
    minWidth: 80
  }
};
